import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import ROUTES from '@src/constants/route';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import iconUpgrade from '@src/assets/images/icon-paid-package.png';
import { PACKAGE_LEVEL_V3, PACKAGE_TYPE } from '@src/constants/package';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import { MOE_PAYWALL_CTA, MOE_PAYWALL_TYPE } from '@src/constants/moengage';

const OutOfCreditsStandard = ({ open, onClose, voice = {} }) => {
  const eolVoice = voice?.eolDate;
  const { t } = useTranslation();
  const history = useHistory();
  const { getUpgradePackageCode, packages } = useMappingPackage();
  const { usingPackage } = useSelector((state) => state.user);
  const { type, expiresIn } = usingPackage || {};
  const { trackCta } = usePaywallTracking({
    paywallType: MOE_PAYWALL_TYPE.OUT_OF_CREDITS,
  });
  const handleBuyMore = () => {
    const topupPackageCode = packages.find(
      (p) =>
        p.type === PACKAGE_TYPE.STUDIO &&
        p.level === PACKAGE_LEVEL_V3.STANDARD &&
        p.isTopUp,
    )?.code;
    trackCta(MOE_PAYWALL_CTA.TOPUP, { targetPackageCode: topupPackageCode });
    const url = `${ROUTES.PAYMENT}?package=${topupPackageCode}`;
    history.push(url);
  };

  // Resolve the upgrade target from the current package's own level/type/
  // expiresIn (version-agnostic), then deep-link to it. Fall back to the generic
  // payment page only if no matching package exists, so the button is never a
  // dead no-op.
  const redirectToUpgrade = (upgradePackage) => {
    trackCta(MOE_PAYWALL_CTA.UPGRADE, { targetPackageCode: upgradePackage });
    const url = upgradePackage
      ? `${ROUTES.PAYMENT}?package=${upgradePackage}`
      : ROUTES.PAYMENT;
    history.push(url);
  };

  const handleUpgradeNow = () => {
    // Next tier up from the current package (STANDARD -> SPECIAL).
    redirectToUpgrade(
      getUpgradePackageCode({ level: usingPackage?.level, type, expiresIn }),
    );
  };

  const handleUpgradeVIP = () => {
    redirectToUpgrade(
      getUpgradePackageCode({
        type,
        expiresIn,
        targetLevel: PACKAGE_LEVEL_V3.VIP,
      }),
    );
  };

  const renderActionButton = () => {
    if (eolVoice) {
      return (
        <>
          <Button variant="contained" onClick={onClose}>
            {t('gotIt')}
          </Button>
          <Button className="outlined-button" onClick={handleUpgradeVIP}>
            {t('upgradePackage')}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button className="outlined-button" onClick={handleUpgradeNow}>
          {t('upgradePackage')}
        </Button>
        <Button variant="contained" onClick={handleBuyMore}>
          {t('buyMoreChar')}
        </Button>
      </>
    );
  };

  const renderDescription = () => {
    if (eolVoice) {
      return t('descriptionOutOfCreditsStandardEOL');
    }
    return t('descriptionOutOfCreditsStandard');
  };

  return (
    <NotificationDialogV4
      image={iconUpgrade}
      title={t('outOfCredits')}
      description={renderDescription()}
      actionComponent={renderActionButton()}
      open={open}
      onClose={onClose}
    />
  );
};

export default OutOfCreditsStandard;
